import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';

interface SendModalState {
  isOpen: boolean;
  packingListId: string;
  packingListNumber: string;
  buyerName: string;
}

const initialSendModal: SendModalState = { isOpen: false, packingListId: '', packingListNumber: '', buyerName: '' };

export const PackingListList: React.FC = () => {
  const [lists, setLists] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [sendModal, setSendModal] = useState<SendModalState>(initialSendModal);
  const [sendEmail, setSendEmail] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = () => {
    fetch('/api/admin/packing-lists', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setLists(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id: string, number: string) => {
    if (!confirm(`Delete packing list ${number}?`)) return;
    await fetch('/api/admin/packing-lists', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    setLists(prev => prev.filter(l => l.id !== id));
  };

  const openSendModal = (list: any) => {
    setSendModal({ isOpen: true, packingListId: list.id, packingListNumber: list.packingListNumber, buyerName: list.buyerName || 'Unassigned' });
    setSendEmail('');
    setSendCc('');
    setSendError('');
    setSending(false);
  };

  const closeSendModal = () => {
    setSendModal(initialSendModal);
    setSendCc('');
    setSendError('');
  };

  const handleSend = async () => {
    const emailList = sendEmail.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    if (!emailList.length) {
      setSendError('At least one staff email is required.');
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailList.some(e => !emailRe.test(e))) {
      setSendError('Please enter valid email addresses, separated by commas.');
      return;
    }

    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`/api/admin/packing-lists?id=${sendModal.packingListId}&action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: emailList.join(','), cc: sendCc.trim() || undefined }),
      });
      if (res.ok) {
        closeSendModal();
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        setSendError(data.error || 'Failed to send packing list. Please try again.');
      }
    } catch {
      setSendError('Network error. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Packing Lists</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{lists.length} total &middot; bags &amp; weights for staff, no pricing</p>
        </div>
        <Link to="/admin/packing-lists/new" className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10">
          + New Packing List
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />)}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Packing List</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Buyer</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Mark</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Bags / Weight</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lists.map(list => (
                <tr key={list.id} className="border-b border-[#E8E6E2]/60 last:border-0 hover:bg-[#FAFAF8]/50 transition-colors">
                  <td className="px-5 py-4 font-semibold text-[#1A1A1A]">{list.packingListNumber}</td>
                  <td className="px-5 py-4 text-[#1A1A1A]">{list.buyerName || <span className="text-[#C4C4C4]">Unassigned</span>}</td>
                  <td className="px-5 py-4 text-[#6B6B6B]">{list.shippingMark || <span className="text-[#C4C4C4]">&mdash;</span>}</td>
                  <td className="px-5 py-4 text-[#6B6B6B] tabular-nums">
                    {list.totalBags?.toLocaleString('en-US') || 0} bags
                    <span className="text-[#C4C4C4]"> &middot; {parseFloat(list.totalNetWeightKg || '0').toLocaleString('en-US')} kg</span>
                  </td>
                  <td className="px-5 py-4"><StatusBadge status={list.status} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`/api/admin/packing-lists?id=${list.id}&action=pdf`}
                        target="_blank"
                        rel="noopener"
                        className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors"
                        title="View PDF"
                      >
                        PDF
                      </a>
                      <Link to={`/admin/packing-lists/${list.id}`} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">
                        Edit
                      </Link>
                      <button onClick={() => openSendModal(list)} className="px-2.5 py-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors">
                        {list.status === 'draft' ? 'Send' : 'Resend'}
                      </button>
                      {list.status === 'draft' && (
                        <button onClick={() => handleDelete(list.id, list.packingListNumber)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {lists.length === 0 && (
            <div className="p-12 text-center"><p className="text-[#A3A3A3]">No packing lists yet. Create your first one.</p></div>
          )}
        </div>
      )}

      {sendModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeSendModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
            <div className="px-6 py-5 border-b border-[#E8E6E2]/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A]">Send Packing List</h2>
                  <p className="text-sm text-[#A3A3A3] mt-0.5">{sendModal.packingListNumber} &middot; {sendModal.buyerName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/api/admin/packing-lists?id=${sendModal.packingListId}&action=pdf`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview the PDF before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6s1.5-4 5-4 5 4 5 4-1.5 4-5 4-5-4-5-4z" stroke="currentColor" strokeWidth="1.1"/><circle cx="6" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.1"/></svg>
                    PDF
                  </a>
                  <a
                    href={`/api/admin/packing-lists?id=${sendModal.packingListId}&action=email-preview`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview the email body before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 2.5h9v7h-9v-7z" stroke="currentColor" strokeWidth="1.1"/><path d="M1.5 3l4.5 3.5L10.5 3" stroke="currentColor" strokeWidth="1.1"/></svg>
                    Email
                  </a>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">Staff Email(s)</label>
                <input
                  type="text"
                  value={sendEmail}
                  onChange={e => { setSendEmail(e.target.value); setSendError(''); }}
                  placeholder="warehouse@lot20.coffee, another@lot20.coffee"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                  autoFocus
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Separate multiple addresses with commas.</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">CC (optional)</label>
                <input
                  type="text"
                  value={sendCc}
                  onChange={e => { setSendCc(e.target.value); setSendError(''); }}
                  placeholder="yourname@example.com"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                />
              </div>
              {sendError && (
                <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl"><p className="text-sm text-red-600 font-medium">{sendError}</p></div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E8E6E2]/60 bg-[#FAFAF8]/50 flex items-center justify-end gap-2">
              <button onClick={closeSendModal} disabled={sending} className="px-4 py-2 text-sm font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-white rounded-lg border border-[#E8E6E2] transition-all">Cancel</button>
              <button onClick={handleSend} disabled={sending || !sendEmail.trim()} className="px-5 py-2 text-sm font-medium text-white bg-[#1A1A1A] rounded-xl hover:bg-[#2a2a2a] disabled:opacity-40 transition-all shadow-sm shadow-black/10">
                {sending ? 'Sending...' : 'Send Packing List'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
